// Which files a purchased tier unlocks, and which entitlement records a tiered
// product has to keep on disk for that answer to stay right over time.
//
// Two callers share this and they have to agree exactly:
//   - /api/download resolves the bytes, so it is the enforcement point.
//   - the product editor writes `tierFiles`, so it decides what the enforcement
//     point will see.
//
// They disagreed twice before this module existed, both times in a way that
// only showed up after the fact:
//   1. The editor reads an ABSENT tier record as "this tier unlocks the whole
//      pool" (an untouched tier ships everything), while /api/download reads
//      absent as "unlocks nothing". So deleting a tier from the price list, or
//      never writing its record, silently revoked files its buyers had paid
//      for. buildTierFiles is the fix: it always emits an explicit list per
//      live tier AND carries retired tiers forward.
//   2. /api/download gated the shared-pool shape but not the legacy per-tier
//      `files` map, so a cheapest-tier buyer of an unmigrated product could
//      pull every tier's files. downloadCandidates gates both shapes.
//
// Pure: no React, no Firestore, no network. Both callers pass in what they hold.

/** A deliverable as the shared pool stores it. `id` is what tierFiles references. */
export type PoolEntry = { id: string; name: string; path?: string; size?: number };

/** A deliverable as the legacy per-tier `files` map stores it (no stable id). */
export type LegacyEntry = { name: string; path?: string; size?: number };

/** What /api/download matches the requested filename against. */
export type DownloadableFile = { id?: string; name: string; path?: string; size?: number };

export type TieredProductShape = {
    files?: { [tierId: string]: LegacyEntry[] } | null;
    productFiles?: PoolEntry[] | null;
    tierFiles?: { [tierId: string]: string[] } | null;
    pricingModel?: string;
};

/**
 * Why a resolution did not gate cleanly. Both values are worth logging: they
 * describe product/order shapes that should be shrinking to zero, and the
 * second one means a buyer is about to get a 404 they do not deserve.
 */
export type TierGateNote =
    /** Tiered product, but the order predates tier recording: whole pool served. */
    | 'legacy-order'
    /** Tiered product on the pool model carrying no tierFiles map at all: fails closed. */
    | 'missing-tier-map'
    | null;

export type TierGateResult = { candidates: DownloadableFile[]; note: TierGateNote };

/**
 * The files a buyer of `purchasedTierId` may resolve on this product.
 *
 * Shared-pool products gate on `tierFiles`. An absent per-tier record fails
 * closed (`?? []`), and so does an absent tierFiles MAP: the two have to behave
 * the same or "no entitlement recorded" would mean "nothing" for one product and
 * "everything" for another. The editor never writes a tiered product without the
 * map, so failing closed here costs nothing and removes a fail-open branch.
 *
 * Legacy per-tier `files` products gate on the map's own keys, because that map
 * IS the entitlement: bucket `{tierId}` plus the `default` bucket, which was
 * always "files every tier gets" (see normalizeFilePool). This branch does not
 * empty out on its own: backfillTierFilesModel deliberately SKIPS any product
 * with a cross-tier filename collision and leaves it on this map, so it only
 * migrates when the seller re-saves through the editor.
 *
 * The one deliberate ungated case is a tiered product bought before tier
 * recording shipped: there is no tier to scope to, and the buyer does own the
 * product, so serving the whole pool beats breaking a legitimate download.
 */
export function downloadCandidates(
    product: TieredProductShape,
    purchasedTierId: number | string | null | undefined,
): TierGateResult {
    const tiered = product.pricingModel === 'tiered';
    const pool = Array.isArray(product.productFiles) ? product.productFiles : [];

    if (pool.length > 0) {
        if (!tiered) return { candidates: pool, note: null };
        if (purchasedTierId == null) return { candidates: pool, note: 'legacy-order' };
        const map = product.tierFiles;
        const allowed = new Set(map?.[String(purchasedTierId)] ?? []);
        return {
            candidates: pool.filter(f => allowed.has(f.id)),
            note: map ? null : 'missing-tier-map',
        };
    }

    const legacy = (product.files ?? {}) as { [tierId: string]: LegacyEntry[] };
    const bucket = (key: string): LegacyEntry[] =>
        Array.isArray(legacy[key]) ? legacy[key] : [];
    if (tiered && purchasedTierId != null) {
        return { candidates: [...bucket(String(purchasedTierId)), ...bucket('default')], note: null };
    }
    return {
        candidates: Object.values(legacy).filter(Array.isArray).flat(),
        note: tiered ? 'legacy-order' : null,
    };
}

/**
 * The display name of a purchased tier, for telling a buyer which tier their
 * file list is scoped to. Empty when the tier can't be named — no tier
 * recorded (a legacy order), or a tier the seller has since removed from the
 * price list. Both cases still serve files (retired tiers keep their tierFiles
 * record, see buildTierFiles), so the caller must fall back to unnamed wording
 * rather than treating "no name" as "no access".
 *
 * Ids are compared as strings because the product doc stores them as numbers
 * while entitlements and cart items carry them as either.
 */
export function tierDisplayName(
    priceTiers: unknown,
    tierId: number | string | null | undefined,
): string {
    if (!Array.isArray(priceTiers) || tierId == null) return '';
    const wanted = String(tierId);
    for (const tier of priceTiers) {
        if (!tier || typeof tier !== 'object') continue;
        const { id, name } = tier as { id?: unknown; name?: unknown };
        if (id == null || String(id) !== wanted) continue;
        return typeof name === 'string' ? name : '';
    }
    return '';
}

export type BuildTierFilesParams = {
    /** The tiers that survive on the price list after this edit. */
    priceTiers: ReadonlyArray<{ id: number | string }>;
    /** The editor's in-memory tierFiles, keyed by tier id, holding display names. */
    tierFiles: Readonly<Record<string, string[]>> | undefined | null;
    /** Every id in the pool being saved (persisted survivors + fresh uploads). */
    poolIds: readonly string[];
    /** Ids of pool files that already existed, i.e. entries that are already ids. */
    persistedIds: ReadonlySet<string>;
    /** Display name -> pool id, for the files uploaded by this same save. */
    newNameToId: ReadonlyMap<string, string>;
};

/**
 * The `tierFiles` map to persist, resolved from the checklist's display names to
 * stable pool ids.
 *
 * A record is written for EVERY live tier, never left absent: /api/download
 * reads a missing record as "this tier unlocks nothing", so an unwritten tier
 * would lock its buyers out.
 *
 * RETIRED tiers are carried forward for exactly the same reason. A tier removed
 * from the price list is gone as a thing to buy, but every past buyer's
 * users/{uid}/ownedProducts/{productId}.tierId still points at it, and nothing
 * server-side ever rewrites that id (the only writer is the subscription
 * tier-change path). Dropping its record would 404 those buyers forever on files
 * that are still in the pool and still in storage, which is the opposite of what
 * the tier-retirement email promises them ("You keep everything you have already
 * paid for"). Records whose files have all left the pool are dropped, since
 * their storage objects are deleted on save and there is nothing left to serve.
 */
export function buildTierFiles(params: BuildTierFilesParams): Record<string, string[]> {
    const { priceTiers, tierFiles, poolIds, persistedIds, newNameToId } = params;

    const toPoolId = (entry: string): string | undefined =>
        newNameToId.get(entry) ?? (persistedIds.has(entry) ? entry : undefined);
    const resolve = (selection: string[]): string[] =>
        selection.map(toPoolId).filter((x): x is string => !!x);

    const out: Record<string, string[]> = {};
    for (const tier of priceTiers ?? []) {
        const tid = String(tier.id);
        const selection = tierFiles?.[tid];
        // No record = the seller never narrowed this tier, which is what the
        // checklist showed them (every box ticked), and what the legacy download
        // path did for every tier anyway. So it keeps the whole pool.
        out[tid] = Array.isArray(selection) ? resolve(selection) : [...poolIds];
    }

    for (const [tid, selection] of Object.entries(tierFiles ?? {})) {
        if (tid in out || !Array.isArray(selection)) continue;
        const ids = resolve(selection);
        if (ids.length > 0) out[tid] = ids;
    }
    return out;
}

export type TiersWithNoDownloadsParams = {
    /** The tiers on the price list being launched. */
    priceTiers: ReadonlyArray<{ id: number | string }>;
    /** The editor's in-memory selection, keyed by tier id. No record = every pool file. */
    tierFiles: Readonly<Record<string, readonly string[]>> | undefined | null;
    /**
     * Every key a selection entry can match in the pool being saved: the same
     * keys the checklist stores (file names for fresh uploads, pool ids for files
     * that were already saved). An entry naming a file that has left the pool
     * counts for nothing, exactly as buildTierFiles drops it.
     */
    poolKeys: readonly string[];
    /** Access links are product-wide, so they reach every tier. */
    hasAccessLinks: boolean;
};

/**
 * Ids of the tiers that would sell a buyer nothing: the seller unticked every
 * file for the tier, and the product has no access link to deliver instead.
 *
 * buildTierFiles keeps such a tier empty on purpose (an empty list is a
 * deliberate choice and must not be read back as "everything"), and
 * downloadCandidates then serves nothing for it. Nothing stopped that reaching
 * a buyer, who paid and found an empty download list, so the editor blocks the
 * launch on this instead.
 *
 * A product with no files at all is left to the editor's own "add a file or an
 * access link" check, which already covers it.
 */
export function tiersWithNoDownloads(params: TiersWithNoDownloadsParams): string[] {
    const { priceTiers, tierFiles, poolKeys, hasAccessLinks } = params;
    if (hasAccessLinks || poolKeys.length === 0) return [];
    const pool = new Set(poolKeys);
    const empty: string[] = [];
    for (const tier of priceTiers ?? []) {
        const tid = String(tier.id);
        const selection = tierFiles?.[tid];
        // No record: the seller never narrowed this tier, so it ships the pool.
        if (!Array.isArray(selection)) continue;
        if (!selection.some((key) => pool.has(key))) empty.push(tid);
    }
    return empty;
}
